<script setup lang="ts">
import { useFetch } from '@/composables/useFetch';
// import { PERMISSIONS, type PermissionCategory } from '@/constants/permissions';
import { useManager } from '@/composables/useManager'; // Import useManager
import { useOnboarding } from '@/composables/useOnboarding';
import { useSubscription } from '@/composables/useSubscription';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Employee } from '@/types/api'; // Import Employee type
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref } from 'vue'; // Import computed and onMounted
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const envStore = useEnvStore();
const { fetchData } = useFetch();
const { getTeam } = useManager(); // Destructure getTeam
const showDialog = ref(false);
const registrationUrl = ref('');
const router = useRouter();
const { maxEmployeesAllowed, canAddEmployee } = useSubscription();
const onboarding = useOnboarding();

const employeeForm = ref({
  first_name: '',
  last_name: '',
  email: '',
  role: 'employee', // default role
  permissions: [] as string[],
});

const errors = ref({
  first_name: '',
  last_name: '',
  email: '',
  role: '',
});

const isLoading = ref(false);
const team = ref<Employee[]>([]); // Add team ref

// Computed properties for employee limit
// const maxEmployeesAllowed = computed(() => {
//   return userStore.user?.active_restaurant?.subscription?.plan?.features?.employees_included ?? 0;
// });

// const activeTeam = computed(() => {
//   return team.value.filter((member) => member.registration_completed && member.id !== userStore.user?.id);
// });

// const pendingTeam = computed(() => {
//   return team.value.filter((user) => !user.registration_completed);
// });

// const currentEmployeeCount = computed(() => {
//   return activeTeam.value.length + pendingTeam.value.length;
// });

// const canAddEmployee = computed(() => {
//   // TODO : Implement a more robust subscription check
//   if (!userStore.isSubscribed) {
//     return false;
//   }
//   return currentEmployeeCount.value < maxEmployeesAllowed.value;
// });

const validateForm = () => {
  let isValid = true;
  errors.value = {
    first_name: '',
    last_name: '',
    email: '',
    role: '',
  };

  if (!employeeForm.value.first_name) {
    errors.value.first_name = t('auth.user.form.error.required');
    isValid = false;
  }

  if (!employeeForm.value.last_name) {
    errors.value.last_name = t('auth.user.form.error.required');
    isValid = false;
  }

  if (!employeeForm.value.email) {
    errors.value.email = t('auth.user.form.error.required');
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeForm.value.email)) {
    errors.value.email = t('auth.user.form.error.invalid_email');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) return;
  if (onboarding.isOnboardingActive.value) {
    console.log('[Onboarding] Ajout employé simulé.');
    isLoading.value = true;

    // Simuler un délai réseau
    await new Promise((resolve) => setTimeout(resolve, 800));

    isLoading.value = false;

    // Simuler le succès
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.success.created'),
      life: 3000,
    });

    // Afficher le dialogue de succès avec une fausse URL
    registrationUrl.value = 'https://gastronomia.app/invite/onboarding-example';
    showDialog.value = true;

    // Nettoyer le formulaire
    employeeForm.value = {
      first_name: '',
      last_name: '',
      email: '',
      role: 'employee',
      permissions: [],
    };

    return; // STOPPER ICI pour ne pas appeler l'API réelle
  }
  if (!userStore.user?.restaurants[0].id) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.error.restaurantRequired'),
      life: 3000,
    });
    return;
  }

  isLoading.value = true;
  try {
    const { data, error } = await fetchData(
      `${envStore.apiUrl}/manager/${userStore.user.active_restaurant?.restaurant_user_id}/employees/create`,
      'POST',
      {
        ...employeeForm.value,
        restaurant_id: userStore.user.restaurants[0].id,
      },
    );

    if (error) {
      console.log('Error creating employee:', error);
      // Check if we have a structured error response with email validation errors
      if (error?.details?.errors) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: error.details.message,
          life: 3000,
        });
        // Set the email field error for form validation
        if (error?.details?.errors?.email) {
          errors.value.email = error.details.errors.email[0];
        }
      } else {
        // Generic error message for other types of errors
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: t('employee.error.creationError'),
          life: 3000,
        });
      }
      return;
    }

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.success.created'),
      life: 3000,
    });

    if (data?.registration_url) {
      registrationUrl.value = data.registration_url;
      showDialog.value = true;
    }

    // Reset form
    employeeForm.value = {
      first_name: '',
      last_name: '',
      email: '',
      role: 'employee',
      permissions: [],
    };
  } catch (err) {
    console.error('Error creating employee:', err);
  } finally {
    isLoading.value = false;
  }
};

const copyInvitationLink = () => {
  navigator.clipboard
    .writeText(registrationUrl.value)
    .then(() => {
      toast.add({
        severity: 'info',
        summary: t('common.success'),
        detail: t('employee.invitationLink.copied'),
        life: 3000,
      });
    })
    .catch((err) => {
      console.error('Could not copy text: ', err);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.error.copyFailed'),
        life: 3000,
      });
    });
};

const returnToTeam = () => {
  showDialog.value = false;
  router.go(-1); // Go back to the previous view
};

onMounted(async () => {
  if (onboarding.isOnboardingActive.value) {
    employeeForm.value.first_name = 'Jean';
    employeeForm.value.last_name = 'Exemple';
    employeeForm.value.email = 'jean.exemple@email.com';
  }
  if (userStore.user?.active_restaurant?.id) {
    const { data, error } = await getTeam(userStore.user.active_restaurant.id);
    if (data) {
      team.value = data.users;
    }
    if (error) {
      console.error('Failed to fetch team for limit check:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('team.errors.fetchError'), // Add i18n key
        life: 3000,
      });
      router.go(-1); // Go back if team data can't be fetched
      return;
    }
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.error.restaurantRequired'),
      life: 3000,
    });
    router.go(-1); // Go back if no active restaurant
    return;
  }

  if (!canAddEmployee.value) {
    toast.add({
      severity: 'warn',
      summary: t('team.notifications.limitReachedTitle'),
      detail: t('team.notifications.limitReachedDetail', { count: maxEmployeesAllowed.value }),
      life: 4000,
    });
    router.go(-1);
  }
});
</script>

<template>
  <main class="onboarding-add-employee-view p-4">
    <h1 class="mb-6 font-bold text-2xl">{{ t('employee.addEmployee') }}</h1>

    <form class="max-w-lg space-y-4" @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-2">
        <label for="firstName">{{ t('auth.user.form.label.first_name') }} *</label>
        <div>
          <InputText id="firstName" v-model="employeeForm.first_name" :class="{ 'p-invalid': errors.first_name }" class="w-full" />
        </div>
        <small class="text-red-500">{{ errors.first_name }}</small>
      </div>

      <div class="flex flex-col gap-2">
        <label for="lastName">{{ t('auth.user.form.label.last_name') }} *</label>
        <div>
          <InputText id="lastName" v-model="employeeForm.last_name" :class="{ 'p-invalid': errors.last_name }" class="w-full" />
        </div>
        <small class="text-red-500">{{ errors.last_name }}</small>
      </div>

      <div class="flex flex-col gap-2">
        <label for="email">{{ t('auth.user.form.label.mail') }} *</label>
        <div>
          <InputText id="email" v-model="employeeForm.email" type="email" :class="{ 'p-invalid': errors.email }" class="w-full" />
        </div>
        <small class="text-red-500">{{ errors.email }}</small>
      </div>

      <div class="mt-6 flex justify-end">
        <Button type="submit" :label="t('employee.form.submit')" :loading="isLoading" class="onboarding-add-employee-confirm w-full" />
      </div>
    </form>
    <Dialog v-model:visible="showDialog" modal :header="t('common.success')" class="onboarding-add-employee-dialog" :style="{ maxWidth: '90vw' }">
      <div class="p-4">
        <p class="mb-4">{{ t('employee.success.created') }}</p>
        <div class="mb-4 break-all rounded-lg bg-gray-100 p-3 text-sm">
          {{ registrationUrl }}
        </div>
        <p class="text-sm text-gray-600">{{ t('employee.invitationLink.invitationLinkInfo') }}</p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('employee.invitationLink.copy')"
            icon="pi pi-copy"
            class="p-button-secondary"
            :class="{ 'onboarding-disabled-button': onboarding.isOnboardingActive.value }"
            :disabled="onboarding.isOnboardingActive.value"
            @click="copyInvitationLink"
          />
          <Button class="onboarding-add-employee-dialog-confirm" :label="t('common.ok')" icon="pi pi-check" autofocus @click="returnToTeam" />
        </div>
      </template>
    </Dialog>
  </main>
</template>
